import React, { useState, useEffect } from 'react';
import { Heart, X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData, images } from '../mockData';

const Gallery = () => {
  const { language } = useLanguage();
  const content = weddingData[language].gallery;
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') navigateImage('next');
      if (e.key === 'ArrowLeft') navigateImage('prev');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedImage]);

  const openLightbox = (image, index) => {
    setSelectedImage({ ...image, index });
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    if (!selectedImage) return;
    const newIndex = direction === 'next' 
      ? (selectedImage.index + 1) % images.gallery.length
      : (selectedImage.index - 1 + images.gallery.length) % images.gallery.length;
    setSelectedImage({ ...images.gallery[newIndex], index: newIndex });
  };

  return (
    <div className="min-h-screen pt-16 sm:pt-20 pb-12 sm:pb-20 bg-white">
      {/* ═══════════════════════════════════════════════════════════════
          HEADER - Fond lilas doux
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-gradient-to-b from-lilac-100 via-lilac-200 to-lilac-100 py-20 sm:py-28 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-white/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 sm:w-72 h-48 sm:h-72 bg-gold-200/20 rounded-full blur-3xl" />
        </div>

        <div className={`container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Camera className="mx-auto text-gold-500 w-12 h-12 sm:w-16 sm:h-16 mb-6" />
          <h1 className="font-playfair font-bold text-plum-900 mb-4 sm:mb-6">
            {content.title}
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-plum-700 font-montserrat max-w-2xl mx-auto px-4">
            {content.subtitle}
          </p>
          <div className="divider-gold mt-6 sm:mt-8" />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          GALLERY - Blanc pur avec photos encadrées
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-playfair font-bold text-plum-900 mb-4">
              {content.teasing.title}
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-plum-600 font-montserrat max-w-xl mx-auto px-4">
              {content.teasing.description}
            </p>
          </div>

          {/* Gallery Grid - Photos encadrées */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-7xl mx-auto">
            {images.gallery.map((image, index) => {
              const isLarge = [0, 2].includes(index);
              return (
              <div
                key={index}
                className={`group cursor-pointer transition-all duration-700
                           ${isLarge ? 'md:col-span-2 md:row-span-2' : ''}`}
                onClick={() => openLightbox(image, index)}
                style={{
                  transitionDelay: `${index * 50}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
                }}
              >
                {/* Photo Frame - Cadre blanc avec ombre */}
                <div className="bg-white p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-photo 
                               group-hover:shadow-elevated group-hover:-translate-y-2 transition-all duration-500">
                  <div className="aspect-square overflow-hidden rounded-lg sm:rounded-xl">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      style={{ filter: 'brightness(1.02) contrast(1.02) saturate(1.05)' }}
                      loading="lazy"
                    />
                  </div>
                </div>
                
                {/* Caption on hover */}
                <div className="mt-2 sm:mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-center">
                  <p className="text-plum-700 font-montserrat text-xs sm:text-sm line-clamp-1 px-1">
                    {image.caption}
                  </p>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          COMING SOON
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-12 sm:py-20 lg:py-28 bg-gradient-to-b from-white via-lilac-50/50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl sm:rounded-3xl text-center p-8 sm:p-12 lg:p-16 shadow-medium border border-lilac-100">
              <Heart className="mx-auto text-gold-500 fill-gold-500 w-12 h-12 sm:w-16 sm:h-16 mb-6 animate-pulse" />
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-playfair font-bold text-plum-900 mb-4">
                {content.wedding.title}
              </h2>
              <div className="divider-gold" />
              <p className="text-base sm:text-lg text-plum-700 font-montserrat leading-relaxed mb-8 px-4">
                {content.wedding.description}
              </p>
              <div className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-lilac-50 rounded-full border border-lilac-200">
                <Camera className="w-5 h-5 text-gold-600" />
                <span className="text-plum-900 font-montserrat font-semibold text-sm sm:text-base">
                  {content.wedding.comingSoon}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          LIGHTBOX
          ═══════════════════════════════════════════════════════════════ */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="text-white w-6 h-6 sm:w-8 sm:h-8" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
            className="absolute left-2 sm:left-4 z-10 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="text-white w-6 h-6 sm:w-8 sm:h-8" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
            className="absolute right-2 sm:right-4 z-10 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="text-white w-6 h-6 sm:w-8 sm:h-8" />
          </button>

          <div className="max-w-[95vw] sm:max-w-[90vw] max-h-[90vh] px-4" onClick={(e) => e.stopPropagation()}>
            {/* Photo avec cadre blanc dans lightbox */}
            <div className="bg-white p-2 sm:p-4 rounded-xl shadow-2xl">
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-w-full max-h-[75vh] object-contain rounded-lg mx-auto"
                style={{ filter: 'brightness(1.02) contrast(1.02)' }}
              />
            </div>
            <p className="text-white text-center mt-4 font-montserrat text-sm sm:text-base lg:text-lg px-4">
              {selectedImage.caption}
            </p>
            <p className="text-white/60 text-center mt-2 font-montserrat text-xs sm:text-sm">
              {selectedImage.index + 1} / {images.gallery.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
